import { type ReactNode, memo, useCallback, useMemo } from 'react';

import {
  ChartBlock,
  tableDataToMarkdown,
} from '@spotify/confidence-conversation-react';
import { Copy } from '@spotify/confidence-core-react';
import { PulseV1PulseBlockType } from '@spotify/confidence-graphql';

import { useChatContext } from '../ChatThread/ChatContext';
import { MarkdownContent } from '../MarkdownContent';
import { InlineBlockRenderProvider } from '../MarkdownContent/InlineBlockContext';
import { FeedbackToolbar } from './FeedbackToolbar';
import { MessageTimestamp } from './MessageTimestamp';
import { formatMessageForExport } from './formatMessageForExport';
import {
  type InlineMarkerBlock,
  type InlineNumberBlock,
  resolveInlineBlock,
  resolveInlineNumberBlock,
} from './inlineBlocks';

import styles from './AssistantMessage.module.css';

const renderBlockContent = (block: InlineMarkerBlock): ReactNode => {
  if (block.type === PulseV1PulseBlockType.PulseBlockTypeTable) {
    return (
      <MarkdownContent>{tableDataToMarkdown(block.table)}</MarkdownContent>
    );
  }
  if (block.type === PulseV1PulseBlockType.PulseBlockTypeText) {
    return <MarkdownContent>{block.content}</MarkdownContent>;
  }
  return <ChartBlock content={block.spec} />;
};

interface AssistantMessageProps {
  content: string;
  eventName?: string;
  renderBlockActions?: (block: InlineMarkerBlock) => ReactNode;
  renderInlineNumber?: (
    block: InlineNumberBlock,
    value: ReactNode,
  ) => ReactNode;
}

export const AssistantMessage = memo(
  ({
    content,
    eventName,
    renderBlockActions,
    renderInlineNumber,
  }: AssistantMessageProps) => {
    const inlineBlocks = useChatContext()?.inlineBlocks;

    const renderInlineBlock = useCallback(
      (name: string): ReactNode => {
        if (!inlineBlocks) return null;
        const block = resolveInlineBlock(inlineBlocks, name, eventName);
        if (!block) return null;

        return (
          <div className={styles.inlineBlock}>
            {renderBlockActions && (
              <div className={styles.blockActions}>
                {renderBlockActions(block)}
              </div>
            )}
            {renderBlockContent(block)}
          </div>
        );
      },
      [inlineBlocks, eventName, renderBlockActions],
    );

    const renderNumber = useCallback(
      (name: string, value: ReactNode): ReactNode => {
        if (!inlineBlocks || !renderInlineNumber) return value;
        const block = resolveInlineNumberBlock(inlineBlocks, name, eventName);
        if (!block) return value;
        return renderInlineNumber(block, value);
      },
      [inlineBlocks, eventName, renderInlineNumber],
    );

    const inlineRenderers = useMemo(
      () => ({ renderBlock: renderInlineBlock, renderNumber }),
      [renderInlineBlock, renderNumber],
    );

    const copyValue = useCallback(
      () => formatMessageForExport({ content, inlineBlocks, eventName }),
      [content, inlineBlocks, eventName],
    );

    return (
      <div className={styles.root} data-event-name={eventName}>
        <InlineBlockRenderProvider value={inlineRenderers}>
          <MarkdownContent>{content}</MarkdownContent>
        </InlineBlockRenderProvider>
        <div className={styles.actions}>
          <Copy
            value={copyValue}
            label="Copy message as Markdown"
            variant="compact"
          />
          {eventName && (
            <>
              <FeedbackToolbar eventName={eventName} content={content} />
              <MessageTimestamp eventName={eventName} />
            </>
          )}
        </div>
      </div>
    );
  },
);
AssistantMessage.displayName = 'AssistantMessage';
